import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Clock, Calendar, MessageSquare, Hash, Pencil, Check, X, Building, StickyNote, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { lookupCustomer, createCustomer, updateCustomer } from '../../services/api';

const CustomerPanel = ({ conversation, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savedCustomer, setSavedCustomer] = useState(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    notes: ''
  });

  // Load customer data when conversation changes
  useEffect(() => {
    if (!conversation) {
      setSavedCustomer(null);
      return;
    }
    
    const loadCustomer = async () => {
      setLoading(true);
      try {
        const phone = conversation.customer?.phone;
        const email = conversation.customer?.email;
        const messengerId = conversation.customer?.messenger_id;
        
        let customer = null;
        if (phone) {
          customer = await lookupCustomer(phone, null, null);
        } else if (email) {
          customer = await lookupCustomer(null, email, null);
        } else if (messengerId) {
          customer = await lookupCustomer(null, null, messengerId);
        }
        
        if (customer) {
          setSavedCustomer(customer);
          setForm({
            name: customer.name || '',
            email: customer.email || '',
            phone: customer.phone || '',
            company: customer.company || '',
            notes: customer.notes || ''
          });
        } else {
          // Pre-fill form with conversation customer data for new customer creation
          setSavedCustomer(null);
          setForm({
            name: conversation.customer?.name || '',
            email: conversation.customer?.email || '',
            phone: conversation.customer?.phone || '',
            company: conversation.customer?.company || '',
            notes: ''
          });
        }
      } catch {
        setSavedCustomer(null);
        // Still pre-fill form from conversation
        setForm({
          name: conversation.customer?.name || '',
          email: conversation.customer?.email || '',
          phone: conversation.customer?.phone || '',
          company: '',
          notes: ''
        });
      } finally {
        setLoading(false);
      }
    };
    
    loadCustomer();
  }, [conversation?.id]);

  if (!conversation) return null;

  const customer = conversation.customer || {};
  const messages = conversation.messages || [];
  const customerMessages = messages.filter(m => m.sender === 'customer');
  const agentMessages = messages.filter(m => m.sender === 'agent');
  const createdAt = conversation.created_at ? new Date(conversation.created_at) : null;

  const startEdit = () => {
    setForm({
      name: savedCustomer?.name || customer.name || '',
      email: savedCustomer?.email || customer.email || '',
      phone: savedCustomer?.phone || customer.phone || '',
      company: savedCustomer?.company || '',
      notes: savedCustomer?.notes || ''
    });
    setEditing(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      alert('Customer name is required');
      return;
    }
    
    setSaving(true);
    try {
      if (savedCustomer) {
        // Update existing customer in CRM
        await updateCustomer(savedCustomer.id, {
          name: form.name,
          email: form.email,
          company: form.company,
          notes: form.notes
        });
        setSavedCustomer({ ...savedCustomer, ...form });
      } else {
        // Create new customer in CRM
        const newCustomerData = {
          name: form.name,
          email: form.email || null,
          company: form.company || null,
          notes: form.notes || null,
          phone: customer.phone || null,
          messenger_id: customer.messenger_id || null
        };
        const created = await createCustomer(newCustomerData);
        setSavedCustomer(created);
      }
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (e) {
      console.error('Failed to save customer:', e);
      alert(e.response?.data?.detail || 'Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-80 bg-gray-50 border-l border-gray-200 flex flex-col h-full">
      <ScrollArea className="flex-1">
        <div className="p-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex justify-end mb-1">
              {!editing ? (
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={startEdit} data-testid="edit-customer-btn">
                  <Pencil className="w-3.5 h-3.5 text-gray-400" />
                </Button>
              ) : (
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600" onClick={handleSave} disabled={saving} data-testid="save-customer-btn">
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => setEditing(false)} data-testid="cancel-edit-btn">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            {!editing ? (
              <>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <>
                    <div className="text-center mb-4">
                      <div className="w-16 h-16 bg-[#393d8a] rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-3">
                        {savedCustomer?.name?.charAt(0) || customer.avatar || customer.name?.charAt(0) || '?'}
                      </div>
                      <h3 className="font-semibold text-gray-900" data-testid="customer-name">
                        {savedCustomer?.name || customer.name || 'Unknown'}
                      </h3>
                      {savedCustomer ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full mt-1">
                          <Check className="w-3 h-3" /> Saved in CRM
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full mt-1">
                          New Contact
                        </span>
                      )}
                      {createdAt && (
                        <p className="text-sm text-gray-500 mt-1">
                          Since {createdAt.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                    <div className="space-y-3">
                      {(savedCustomer?.email || customer.email) && (
                        <div className="flex items-center gap-3 text-sm">
                          <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                          <span className="text-gray-600 truncate" data-testid="customer-email">
                            {savedCustomer?.email || customer.email}
                          </span>
                        </div>
                      )}
                      {(savedCustomer?.phone || customer.phone) && (
                        <div className="flex items-center gap-3 text-sm">
                          <Phone className="w-4 h-4 text-gray-400 shrink-0" />
                          <span className="text-gray-600" data-testid="customer-phone">
                            {savedCustomer?.phone || customer.phone}
                          </span>
                        </div>
                      )}
                      {savedCustomer?.company && (
                        <div className="flex items-center gap-3 text-sm">
                          <Building className="w-4 h-4 text-gray-400 shrink-0" />
                          <span className="text-gray-600" data-testid="customer-company">
                            {savedCustomer.company}
                          </span>
                        </div>
                      )}
                      {savedCustomer?.notes && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <div className="flex items-start gap-3 text-sm">
                            <StickyNote className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                            <span className="text-gray-600 whitespace-pre-wrap text-xs" data-testid="customer-notes">
                              {savedCustomer.notes}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Save to CRM Prompt for new contacts */}
                    {!savedCustomer && (
                      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-xs text-amber-800 mb-2">
                          Save this contact to remember them in future conversations.
                        </p>
                        <Button 
                          size="sm" 
                          onClick={startEdit}
                          className="w-full bg-[#eeb224] hover:bg-[#eeb224]/90 text-white"
                          data-testid="save-to-crm-btn"
                        >
                          Save to CRM
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Name *</label>
                  <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="edit-name-input" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Email</label>
                  <Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="edit-email-input" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Phone</label>
                  <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="edit-phone-input" disabled={!!customer.phone} />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Company</label>
                  <Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} data-testid="edit-company-input" placeholder="Company name" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Notes</label>
                  <Textarea 
                    value={form.notes} 
                    onChange={e => setForm({...form, notes: e.target.value})} 
                    data-testid="edit-notes-input" 
                    placeholder="Add notes about this customer..."
                    rows={3}
                    className="resize-none text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-[#393d8a]" />
              Conversation Stats
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-[#393d8a]" data-testid="stat-total-messages">{messages.length}</p>
                <p className="text-xs text-gray-500">Messages</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-[#eeb224]" data-testid="stat-customer-messages">{customerMessages.length}</p>
                <p className="text-xs text-gray-500">From Customer</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-[#30a34b]" data-testid="stat-agent-messages">{agentMessages.length}</p>
                <p className="text-xs text-gray-500">From Agent</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className={cn("text-2xl font-bold", conversation.status === 'resolved' ? 'text-green-600' : 'text-gray-700')} data-testid="stat-status">
                  {conversation.status === 'resolved' ? 'Yes' : 'No'}
                </p>
                <p className="text-xs text-gray-500">Resolved</p>
              </div>
            </div>
          </div>

          <div className="mt-4 bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#393d8a]" />
              Details
            </h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Channel</span>
                <span className="font-medium text-gray-900 capitalize">{conversation.channel || 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Priority</span>
                <span className={cn("font-medium capitalize px-2 py-0.5 rounded-full text-xs",
                  conversation.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                  conversation.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                  conversation.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-gray-100 text-gray-600'
                )}>
                  {conversation.priority || 'normal'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Status</span>
                <span className={cn("font-medium capitalize px-2 py-0.5 rounded-full text-xs",
                  conversation.status === 'resolved' ? 'bg-green-100 text-green-700' :
                  conversation.status === 'open' ? 'bg-blue-100 text-blue-700' :
                  'bg-yellow-100 text-yellow-700'
                )}>
                  {conversation.status || 'open'}
                </span>
              </div>
              {createdAt && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Created</span>
                  <span className="text-gray-700 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {createdAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
              )}
              {conversation.id && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">ID</span>
                  <span className="text-gray-500 font-mono text-xs flex items-center gap-1">
                    <Hash className="w-3 h-3" />
                    {conversation.id.slice(0, 12)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* CRM Stats - only shown when customer is saved */}
          {savedCustomer && (
            <div className="mt-4 bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Hash className="w-4 h-4 text-[#393d8a]" />
                Customer Record
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-[#393d8a]" data-testid="crm-conversation-count">
                    {savedCustomer.conversation_count || 1}
                  </p>
                  <p className="text-xs text-gray-500">Total Chats</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-xs font-medium text-gray-700" data-testid="crm-first-contact">
                    {savedCustomer.created_at 
                      ? new Date(savedCustomer.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                      : '-'}
                  </p>
                  <p className="text-xs text-gray-500">First Contact</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default CustomerPanel;
